<template>
  <div class="final-docs">
    <Collapse class="final-docs__collaspe">
      <div
        slot="title"
        class="final-docs__collaspe-title-box"
      >
        <span class="final-docs__collaspe-title default-title">
          {{ $t('tenantProjects.finalDocuments.title') }}
        </span>
        <a
          class="final-docs__collaspe-subtitle"
          @click="() => $refs.checkListModal.open()"
        >
          {{ $t('tenantProjects.finalDocuments.submissionChecklist') }}
          <i class="fa fa-question-circle" />
        </a>
        <span class="final-docs__collaspe-group float-right">
          <span
            v-if="isCompletedFinalDoc"
            class="final-docs__collaspe-status"
          >
            <i class="final-docs__collaspe-status_check fa fa-check" />
            {{ $t('tenantProjects.final-docs.completed', [$options.filters['formatDate'](completedHistory.createdAt, 'DD MMM YYYY')]) }}
          </span>
        </span>
      </div>
      <div
        slot="content"
        class="final-docs__collaspe-content"
      >
        <div class="row final-docs__bar">
          <div class="small-8">
            <ExemptCheckBox
              v-if="!isHistorical"
            />
          </div>
          <div
            v-if="isHKlandUser || folders.length"
            class="small-4"
          >
            <template v-if="isHKlandUser">
              <div class="final-docs__btns row align-right">
                <HKLButton
                  v-if="showReminderButton"
                  color="darkblue"
                  action="sendReminder"
                  @click="() => openReminderModal()"
                />
                <HKLButton
                  v-if="showCompleteButton"
                  color="darkblue"
                  action="complete"
                  @click="openCompleteModal"
                />
                <template v-if="isFolderFlow && showAddFolderButton">
                  <AddFolder
                    color="orange"
                    action="addFolder"
                    @click="() => openCompleteModal()"
                    @change="() => fetchFolders()"
                  />
                </template>
              </div>
            </template>
            <div
              v-if="folders.length"
              class="final-docs__btns row align-right"
            >
              <HKLButton
                color="orange"
                :action="isCollapseAll ? 'expandAll' : 'collapseAll'"
                @click="() => isCollapseAll ? expandAll() : collapseAll()"
              />
            </div>
          </div>
        </div>
        <div
          v-if="isFolderFlow"
          class="folder-group"
        >
          <template v-if="folders.length === 0">
            <HKLViewLayout :checkIsTenant="true">
              <div class="text-center">
                {{ $t('tenantProjects.final-docs.noFolders') }}
              </div>
            </HKLViewLayout>
          </template>
          <template v-if="folders.length > 0">
            <Collapse
              v-for="(folder, index) of folders"
              :key="folder.id"
              :id="folderCollapseId(folder.id)"
              ref="collapseRefs"
              :expand="!folder.isAccepted"
              @expand-status="onFolderExpandStatus"
            >
              <div
                slot="title"
                class="folder-title-wrapper"
              >
                <div>
                  <span class="default-title">
                    {{ folder.folderName }}
                  </span>
                  <span class="after-hint" v-if="folder.isAfterEnd">
                    &nbsp;{{ $t('tenantProjects.final-docs.addedAfterEnd') }}
                  </span>
                  <DottedDropdown
                    v-if="folder.canDeleteFolder"
                    :id="`folder-${index}`"
                  >
                    <li v-if="folder.canDeleteFolder">
                      <a
                        class="dot-option-btn"
                        @click="() => openDeleteModal(folder.id)"
                      >
                        {{ $t('action.delete') }}
                      </a>
                    </li>
                  </DottedDropdown>
                </div>
                <HKLStatusBadge
                  v-if="folder.showBadge"
                  class="cursor-pointer"
                  :value="$t(folder.status.toLowerCase()) | uppercase"
                  :color="folder.isRejected ? 'red' : 'light-green'"
                  @click="() => lifecycleModalOpen(index)"
                />
              </div>
              <template slot="additional">
                <div
                  v-show="showFolderDescriptionTeaser(folder)"
                  class="folder-description-teaser"
                  :class="{ 'folder-description-teaser--last': index === folders.length - 1 }"
                >
                  <pre
                    class="folder-description-teaser__text"
                    :class="{ 'folder-description-teaser__text--newline': hasFolderDescriptionNewline(folder) }"
                  >{{ folderDescriptionDisplay(folder) }}</pre>
                </div>
              </template>
              <div
                slot="content"
                class="final-docs__collaspe-content"
              >
                <HKLPencilTextarea
                  :canEdit="folder.canEditFolderDescription && isFolderExpanded(folder.id)"
                  :value="folder.description"
                  :defaultHint="isHKlandUser && folder.canEditFolderDescription ? $t('project.docs.fillDescHint') : ''"
                  @close="(newVal) => updateFolder(folder.id, { description: newVal })"
                />
                <HkldDropzone
                  v-if="folder.canUploadFiles"
                  :sourceId="folder.docsSrcId"
                  :metaData="{uploadOnTop: true}"
                />
                <EditAttachments
                  :sourceId="folder.docsSrcId"
                  :canDownloadAll="isHKlandUser"
                  :canDelete="folder.canDeleteFile"
                  :showAllDeleteBtn="false"
                  :uploadOnTop="true"
                  :canEdit="folder.canEditFile"
                  :showDeleteBtnFunc="canDel"
                  :getFilesList="(files) => setFolderFiles(index, files)"
                >
                  <template
                    v-if="folder.canEditFolderDescription && !isFinalDocEnd"
                    #buttons
                  >
                    <HKLButton
                      color="darkblue"
                      action="rejectAction"
                      @click="() => rejectModalOpen(folder.id)"
                    />
                    <HKLButton
                      color="orange"
                      action="accept"
                      @click="() => acceptFolder(folder.id)"
                    />
                  </template>
                </EditAttachments>
              </div>
            </Collapse>
            <HKLModal
              v-if="canDeleteFolder"
              ref="deleteModalRef"
              okBtnAction="delete"
              :title="$t('tenantProjects.final-docs.deleteModalHint')"
              @onOkClick="() => deleteFolder()"
            />
            <HKLModal
              ref="rejectModal"
              okBtnAction="rejectAction"
              :title="$t('tenantProjects.greenFitOut.rejectAlert')"
              :beforeOkClick="formValidate"
              @onOkClick="() => rejectFolder()"
            >
              <HKLForm
                ref="formRef"
                class="text-input"
              >
                <HKLTextarea
                  v-model="rejectReason"
                  v-fusioninputcheck="{
                    type: 'string',
                    required: true
                  }"
                />
              </HKLForm>
            </HKLModal>
            <HKLModal
              ref="lifecycleModal"
              :hasCancelBtn="false"
              :closeable="true"
              size="large"
              :title="$t('history')"
            >
              <HKLTable
                class="lifecycle"
                :dataSource="lifecycleHistories"
                :columns="lifecycleColumns"
                sort="updatedAt"
              />
            </HKLModal>
          </template>
        </div>
        <template v-if="sourceId && !isFolderFlow">
          <HkldDropzone
            v-if="canUpload"
            :sourceId="sourceId"
            :metaData="{uploadOnTop: true}"
          />
          <EditAttachments
            ref="editAttachments"
            :sourceId="sourceId"
            :canDownloadAll="false"
            :canDelete="true"
            :showAllDeleteBtn="false"
            :getFilesList="getFilesList"
            :uploadOnTop="true"
            :canEdit="canEdit"
            :showDeleteBtnFunc="canDel"
          />
        </template>
        <History />
      </div>
    </Collapse>
    <SendReminderModal
      ref="sendReminderModal"
    />
    <SubmissionChecklist ref="checkListModal" />

    <HKLModal
      ref="completeModalRef"
      okBtnAction="yesComplete"
      cancelBtnAction="goback"
      :title="$t('tenantProjects.final-docs.completeHint')"
      @onOkClick="complete"
    />
  </div>
</template>

<script>
import { ref, unref, computed, watch, getCurrentInstance } from 'vue-demi'
import lodash from 'lodash'
import { useStoreGetters, useStoreActions } from '@/store'
import HkldDropzone from '@/controls/HkldDropzone'
import EditAttachments from '@/controls/EditAttachments'
import HKLButton from '@/controls/Button/HKLButton'
import Collapse from '@/controls/Collapse'
import HKLModal from '@/controls/Modal/HKLModal'
import HKLForm from '@/controls/Form/HKLForm'
import HKLTextarea from '@/controls/Input/HKLTextarea.vue'
import { aclHelper, tmsPermsTypes } from '@/utils/aclHelper'
import { sendEmail } from '@/api/postman/request'
import { UserSource, EmailTypes } from '@/utils/consts'
import { useRefModal } from '@/controls/Modal/modal.hook'
import { useRefForm } from '@/controls/Form/hook'
import { tenantProjectStatus } from '@/utils/locale'
import DottedDropdown from '@/controls/DottedDropdown'
import { updateFinalDocsFolder, deleteFinalDocsFolder } from '@/api/tenantProjects/request'
import HKLStatusBadge from '@/controls/Badge/HKLStatusBadge.vue'
import dateUtil from '@/utils/dateUtil'
import i18n from '@/utils/i18n'
import HKLTable from '@/controls/Table/HKLTable.vue'
import HKLViewLayout from '@/controls/Layout/HKLViewLayout.vue'
import HKLPencilTextarea from '@/controls/Input/HKLPencilTextarea.vue'
import { useFinalDocLength } from '../hook'
import { useIsCurrentTenantProjectStatus } from '../../hook'
import { FINAL_DOC_STATUS, FINAL_DOC_FOLDER_STATUS } from '../../consts'
import History from './History'
import SubmissionChecklist from './SubmissionChecklist'
import { useFinalDocHistories, useFinalDocStatus, useFinalDocFoldersQuery } from './hook'
import AddFolder from './AddFolder'
import SendReminderModal from './SendReminderModal'
import ExemptCheckBox from './ExemptCheckBox'

export default {
  name: 'FinalDocuments',
  components: {
    Collapse,
    DottedDropdown,
    EditAttachments,
    HkldDropzone,
    SubmissionChecklist,
    HKLModal,
    HKLButton,
    HKLForm,
    HKLTextarea,
    SendReminderModal,
    ExemptCheckBox,
    History,
    AddFolder,
    HKLPencilTextarea,
    HKLStatusBadge,
    HKLTable,
    HKLViewLayout,
  },
  setup () {
    const modals = useRefModal()

    const openReminderModal = () => {
      modals.open()
    }

    const hasCompleteAccess = computed(() => aclHelper.getAccess(tmsPermsTypes.project.completeFinalDocs))
    const canSendReminder = computed(() => aclHelper.getAccess(tmsPermsTypes.project.sendFinalDocsReminder))
    const { isHKlandUser } = useStoreGetters('user', ['isHKlandUser'])

    const { finalDocument } = useStoreGetters('tenantProject', ['finalDocument'])
    const isCompletedFinalDoc = useFinalDocStatus(FINAL_DOC_STATUS.COMPLETED)
    const isExempted = useFinalDocStatus(FINAL_DOC_STATUS.EXEMPTED)
    const isHistorical = useFinalDocStatus(FINAL_DOC_STATUS.HISTORICAL)
    const histories = useFinalDocHistories()
    const completedHistory = computed(() => {
      const completeHistory = unref(histories).find((item) => item.status === FINAL_DOC_STATUS.COMPLETED)
      return completeHistory
    })
    const exemptedHistory = computed(() => {
      const exemptHistory = unref(histories).find((item) => item.status === FINAL_DOC_STATUS.EXEMPTED)
      return exemptHistory
    })
    const filesLength = useFinalDocLength()
    const getFilesList = (files) => {
      filesLength.value = files.length
    }
    const completeModal = useRefModal()
    const openCompleteModal = () => {
      completeModal.open()
    }
    const { updateFinalDocument } = useStoreActions('tenantProject', ['updateFinalDocument'])
    const complete = async () => {
      await updateFinalDocument({ status: FINAL_DOC_STATUS.COMPLETED })
      if (unref(isCompletedFinalDoc)) {
        await sendEmail(EmailTypes.tenantProjects.completeFinalDocuments)
      }
    }

    const sourceId = computed(() => lodash.get(finalDocument.value, 'docsSrcId'))
    const canUpload = computed(() => !isCompletedFinalDoc.value || unref(isHKlandUser))
    const canDelByStatus = (item) => {
      return (!isCompletedFinalDoc.value) || (unref(isHKlandUser) && item.createdAt > unref(completedHistory).createdAt)
    }

    const canEdit = computed(() => {
      return !unref(isCompletedFinalDoc) || unref(isHKlandUser)
    })
    const canDelByGroup = (item) => {
      const creator = item.creator || {}
      const sameGroup = (unref(isHKlandUser) && creator.source === UserSource.AD) || (!unref(isHKlandUser) && creator.source === UserSource.EXT)
      return sameGroup
    }

    const canDel = (item) => {
      return canDelByStatus(item) && canDelByGroup(item)
    }
    const isSubmittedTProject = useIsCurrentTenantProjectStatus(tenantProjectStatus.SUBMITTED)
    const isInProgressTProject = useIsCurrentTenantProjectStatus(tenantProjectStatus.IN_PROGRESS)
    const showReminderButton = computed(() => {
      const allowedFinalDoc = !unref(isHistorical) || (unref(isHistorical) && (unref(isSubmittedTProject) || unref(isInProgressTProject)))
      return unref(canSendReminder) && !unref(isCompletedFinalDoc) && allowedFinalDoc
    })
    const { loadFinalDocumentHistories } = useStoreActions('tenantProject', ['loadFinalDocumentHistories'])
    watch(() => unref(finalDocument).id, (id) => id && loadFinalDocumentHistories())

    let returnData = {
      showReminderButton,
      isHKlandUser,
      isHistorical,
      isCompletedFinalDoc,
      isExempted,
      finalDocument,
      completedHistory,
      completeModalRef: completeModal.modal,
      openCompleteModal,
      complete,
      hasCompleteAccess,
      sourceId,
      getFilesList,
      filesLength,
      canUpload,
      canDel,
      canSendReminder,
      sendReminderModal: modals.modal,
      canEdit,
      openReminderModal,
    }

    // #region Final Document Folder Flow

    const isFinalDocEnd = computed(() => unref(isCompletedFinalDoc) || unref(isExempted))

    returnData = {
      ...returnData,
      isFinalDocEnd,
    }

    // #region Collapse/Expand Folders

    const { proxy } = getCurrentInstance()
    const isCollapseAll = ref(false)
    const getCollapseRefs = () => proxy.$refs.collapseRefs || []
    const expandAll = () => getCollapseRefs().forEach((r) => r.collapseAndExpand(true))
    const collapseAll = () => getCollapseRefs().forEach((r) => r.collapseAndExpand(false))

    const refreshCollapseStatus = () => {
      const status = getCollapseRefs().every(({ isExpand }) => !isExpand)
      isCollapseAll.value = status
    }

    const FOLDER_COLLAPSE_ID_PREFIX = 'fd-folder-'
    const folderCollapseId = (folderId) => `${FOLDER_COLLAPSE_ID_PREFIX}${folderId}`
    const folderExpandMap = ref({})
    const onFolderExpandStatus = (id, isExpand) => {
      folderExpandMap.value = { ...unref(folderExpandMap), [id]: isExpand }
      refreshCollapseStatus()
    }
    const isFolderExpanded = (folderId) => {
      const collapseId = folderCollapseId(folderId)
      const map = unref(folderExpandMap)
      if (Object.prototype.hasOwnProperty.call(map, collapseId)) {
        return map[collapseId]
      }
      const folder = unref(foldersRef).find((f) => f.id === folderId)
      return folder ? !isFolderAcceptedFn(folder) : true
    }
    const hasFolderDescription = (folder) => !!(folder.description || '').trim()
    const showFolderDescriptionTeaser = (folder) => !isFolderExpanded(folder.id) && hasFolderDescription(folder)
    const folderDescriptionDisplay = (folder) => folder.description || ''
    const hasFolderDescriptionNewline = (folder) => /\n/.test(folder.description || '')

    returnData = {
      ...returnData,
      isCollapseAll,
      expandAll,
      collapseAll,
      refreshCollapseStatus,
      folderCollapseId,
      onFolderExpandStatus,
      isFolderExpanded,
      hasFolderDescription,
      showFolderDescriptionTeaser,
      folderDescriptionDisplay,
      hasFolderDescriptionNewline,
    }

    // #endregion Collapse/Expand Folders

    const finalDocIdRef = computed(() => unref(finalDocument).id)
    const isFolderFlow = computed(() => unref(finalDocument).version === 2)
    const useFinalDocFolders = useFinalDocFoldersQuery(finalDocIdRef, { autoFetchOnMounted: unref(isFolderFlow) && !!unref(finalDocIdRef) })
    const { data: rawFolders, refresh: fetchFolders } = useFinalDocFolders()
    const foldersRef = computed(() => Array.isArray(unref(rawFolders)) ? unref(rawFolders) : [])
    watch(finalDocIdRef, async (id) => {
      if (id && isFolderFlow.value) {
        await fetchFolders()
      }
    })
    const isFolderAcceptedFn = (folder) => folder.status === FINAL_DOC_FOLDER_STATUS.ACCEPTED

    const showCompleteButton = computed(() => {
      const folders = unref(foldersRef)
      return (!unref(isFolderFlow) ? unref(filesLength) : (folders.length && folders.every(isFolderAcceptedFn))) &&
        !unref(isExempted) && !unref(isCompletedFinalDoc) && unref(hasCompleteAccess) && !unref(isHistorical)
    })
    returnData = {
      ...returnData,
      isFolderFlow,
      fetchFolders,
      isFolderAcceptedFn,
      showCompleteButton,
    }

    // #region Add Folders
    const hasAddReviewAccess = computed(() => aclHelper.getAccess(tmsPermsTypes.project.addReviewFinalDocFolder))
    const showAddFolderButton = computed(() => {
      return unref(isFolderFlow) && unref(hasAddReviewAccess) && unref(isHKlandUser)
    })
    returnData = {
      ...returnData,
      hasAddReviewAccess,
      showAddFolderButton,
    }

    // #endregion Add Folders

    // #region Delete Folder

    const canDeleteFolder = computed(() => {
      return unref(isFolderFlow) && unref(hasAddReviewAccess)
    })
    const showFolderActionButtonFn = (folder) => {
      return unref(canDeleteFolder) && unref(isHKlandUser) && !isFolderAcceptedFn(folder)
    }
    const pendingDeleteFolderIdRef = ref(null)
    const deleteModal = useRefModal()
    const openDeleteModal = (id) => {
      pendingDeleteFolderIdRef.value = id
      deleteModal.open()
    }
    const deleteFolder = async () => {
      if (!pendingDeleteFolderIdRef.value) {
        return
      }
      await deleteFinalDocsFolder(unref(finalDocIdRef), unref(pendingDeleteFolderIdRef))
      await fetchFolders()
    }
    returnData = {
      ...returnData,
      canDeleteFolder,
      deleteModalRef: deleteModal.modal,
      openDeleteModal,
      deleteFolder,
    }

    // #endregion Delete Folder

    // #region Update Folder Value

    const { currentUserRef } = useStoreGetters('user', ['currentUser'])
    const updateFolder = async (id, obj) => {
      await updateFinalDocsFolder(unref(finalDocIdRef), id, obj)
      await fetchFolders()
    }
    const acceptFolder = async (id) => {
      const currentUser = unref(currentUserRef)
      const status = FINAL_DOC_FOLDER_STATUS.ACCEPTED
      await updateFolder(id, {
        status: FINAL_DOC_FOLDER_STATUS.ACCEPTED,
        actionHistory: {
          approver: {
            id: currentUser.id,
            name: currentUser.displayName || currentUser.name,
            username: currentUser.username,
          },
          actionType: status,
        },
      })
    }
    const rejectFolderId = ref('')
    const rejectReason = ref('')
    const { form: formRef, validate: formValidate } = useRefForm()
    const { modal: rejectModal, open: rejectModalOpen } = useRefModal()
    const rejectFolderModalOpen = (id) => {
      rejectFolderId.value = id
      rejectReason.value = ''
      rejectModalOpen()
    }
    const rejectFolder = async () => {
      const currentUser = unref(currentUserRef)
      const status = FINAL_DOC_FOLDER_STATUS.REJECTED
      await updateFolder(unref(rejectFolderId), {
        status,
        actionHistory: {
          approver: {
            id: currentUser.id,
            name: currentUser.displayName || currentUser.name,
            username: currentUser.username,
          },
          detail: { rejectReason: unref(rejectReason) },
          actionType: status,
        },
      })
    }
    returnData = {
      ...returnData,
      updateFolder,
      acceptFolder,
      rejectFolder,
      formRef,
      formValidate,
      rejectReason,
      rejectModal,
      rejectModalOpen: rejectFolderModalOpen,
    }

    // #endregion Update Folder Value

    // #region Folder Files

    const folderFilesMap = ref({})
    const setFolderFiles = (index, files) => {
      folderFilesMap.value = { ...folderFilesMap.value, [unref(foldersRef)[index].id]: files }
    }
    const showBadge = (folder) => {
      const { status } = folder
      return [FINAL_DOC_FOLDER_STATUS.ACCEPTED, FINAL_DOC_FOLDER_STATUS.REJECTED].includes(status)
    }

    returnData = {
      ...returnData,
      setFolderFiles,
    }

    // #endregion Folder Files

    const isAfterEnd = (folder) => (unref(isCompletedFinalDoc) && dateUtil.toMoment(unref(completedHistory).createdAt).isSameOrBefore(folder.createdAt)) ||
      (unref(isExempted) && dateUtil.toMoment(unref(exemptedHistory).createdAt).isSameOrBefore(folder.createdAt))

    const folders = computed(() => unref(foldersRef)
      .map((folder) => {
        const files = unref(folderFilesMap)[folder.id]
        return {
          ...folder,
          files,
          canDeleteFolder: showFolderActionButtonFn(folder),
          canEditFolderDescription: showFolderActionButtonFn(folder),
          canUploadFiles: unref(canUpload) && !isFolderAcceptedFn(folder),
          canDeleteFile: !isFolderAcceptedFn(folder),
          canEditFile: unref(canEdit) && !isFolderAcceptedFn(folder),
          isRejected: folder.status === FINAL_DOC_FOLDER_STATUS.REJECTED,
          isAccepted: folder.status === FINAL_DOC_FOLDER_STATUS.ACCEPTED,
          isAfterEnd: isAfterEnd(folder),
          showBadge: showBadge(folder),
        }
      }),
    )
    returnData = {
      ...returnData,
      folders,
    }

    // #region Final Document Folder Life Cycle

    const { modal: lifecycleModal, open: lifecycleModalOpen } = useRefModal()
    const showLifecycleIndex = ref(undefined)
    const lifecycleHistories = computed(() => {
      const histories = lodash.get(folders.value[unref(showLifecycleIndex)], 'actionHistory') || []
      return histories
        .filter((h) => [FINAL_DOC_FOLDER_STATUS.ACCEPTED, FINAL_DOC_FOLDER_STATUS.REJECTED].includes(h.actionType))
        .map((h) => {
          const username = h.approver.name
          const date = dateUtil.format(h.updatedAt, dateUtil.DD_MMM_YY_HH_MM)
          const status = i18n.t(h.actionType.toLowerCase()).toUpperCase()
          const comment = lodash.get(h, 'detail.rejectReason') || ''
          return {
            status,
            comment,
            username,
            date,
            updatedAt: h.updatedAt,
          }
        })
    })
    const lifecycleColumns = [
      {
        title: i18n.t('table.header.date'),
        dataIndex: 'date',
        width: 130,
        emptyValue: i18n.t('emptyValue'),
      },
      {
        title: i18n.t('table.header.action'),
        dataIndex: 'status',
        width: 200,
        emptyValue: i18n.t('emptyValue'),
      },
      {
        title: i18n.t('table.header.actionBy'),
        dataIndex: 'username',
        width: 140,
        emptyValue: i18n.t('emptyValue'),
      },
      {
        title: i18n.t('table.header.comment'),
        dataIndex: 'comment',
        emptyValue: '',
      },
    ]

    returnData = {
      ...returnData,
      lifecycleModal,
      lifecycleModalOpen: (index) => {
        showLifecycleIndex.value = index
        lifecycleModalOpen()
      },
      lifecycleHistories,
      lifecycleColumns,
    }

    // #endregion Final Document Folder Life Cycle

    // #endregion Final Document Folder Flow

    return returnData
  },
}
</script>

<style lang="scss" scoped>
.final-docs {
  margin-top: 20px;
  &__collaspe-subtitle {
    margin-left: 12px;
    font-size: 12px;
  }
  &__collaspe {
    &-group {
      margin-right: 30px;
    }
    &-status {
      color: #3ba04d;
    }
    &-status_check {
      width: 20px;
      height: 20px;
      line-height: 20px;
      border-radius: 50%;
      background-color: #3ba04d;
      color: #ffffff;
      text-align: center;
      margin-right: 10px;
    }
  }
  &__create-btn {
    margin-bottom: 15px;
  }
  .final-docs__collaspe-content {
    padding: 15px;
  }
  .final-docs__bar {
    margin-bottom: 15px;
  }
  .final-docs__btns {
    .btn {
      margin-right: 10px;
    }
    .btn:last-child {
      margin-right: 0;
    }
  }
}

.final-docs__btns {
  & + & {
    margin-top: 5px;
  }
}

.folder-group {
  padding-bottom: 15px;
}

.folder-title-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  :first-child {
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }
}

.text-center {
  text-align: center;
}

.after-hint {
  font-size: 12px;
}

.folder-description-teaser {
  padding: 10px 15px;
  border: 1px solid #b5b5b5;
  &--last {
    border-bottom: 1px solid #b5b5b5;
  }

  &__text {
    margin: 0;
    font-size: 14px;
    font-weight: bold;
    color: #0c3a58;
    display: inline;

    &--newline {
      display: block;
    }
  }
}

</style>
